import type {ScreenText} from '../types/textTypes'
import {computeTextBlockLayout} from './textLayout'

/**
 * Render all screen texts onto an offscreen canvas for export compositing.
 * Positions are normalized 0..1 mapped to the export dimensions.
 */
export const renderTextsToCanvas = (texts: ScreenText[], width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!

  for (const t of texts) {
    const fontStyle = t.italic ? 'italic' : 'normal'
    const fontWeight = t.bold ? '700' : '400'
    // Scale font size relative to export resolution (designed for ~1290-wide base)
    const scaledSize = (t.fontSize / 1290) * width
    ctx.font = `${fontStyle} ${fontWeight} ${scaledSize}px "${t.fontFamily}", sans-serif`
    ctx.fillStyle = t.color
    ctx.textBaseline = 'middle'

    const px = t.x * width
    const py = t.y * height

    ctx.save()
    ctx.translate(px, py)
    if (t.rotation !== 0) {
      ctx.rotate((t.rotation * Math.PI) / 180)
    }

    // Support multi-line text — block vertically centered on the anchor
    const lines = t.text.split('\n')
    const lineHeight = scaledSize * 1.2
    const totalHeight = lines.length * lineHeight
    const startY = -totalHeight / 2 + lineHeight / 2

    // Intra-block alignment: the block stays horizontally centered on the
    // anchor; alignment controls how lines sit within the block.
    const align = t.align ?? 'center'
    if (align === 'center') {
      ctx.textAlign = 'center'
      for (let i = 0; i < lines.length; i++) {
        ctx.fillText(lines[i], 0, startY + i * lineHeight)
      }
    } else {
      const measure = (s: string) => ctx.measureText(s).width
      const layout = computeTextBlockLayout(lines, measure)
      const blockLeft = -layout.maxWidth / 2
      for (let i = 0; i < lines.length; i++) {
        const y = startY + i * lineHeight
        if (align === 'right') {
          ctx.textAlign = 'right'
          ctx.fillText(lines[i], blockLeft + layout.maxWidth, y)
          continue
        }
        ctx.textAlign = 'left'
        const {tokens, gap} = layout.justify[i]
        if (align === 'left' || gap === 0) {
          // Left alignment, or justify fallback for lines without spaces
          ctx.fillText(lines[i], blockLeft, y)
          continue
        }
        // Justify: draw word by word, stretching each space slot to `gap`
        let x = blockLeft
        for (const token of tokens) {
          if (token !== '') ctx.fillText(token, x, y)
          x += measure(token) + gap
        }
      }
    }
    ctx.restore()
  }

  return canvas
}
